// ─────────────────────────────────────────────────────────────────────
// A TOURNAMENT DOES NOT NEED A PLAYER'S ADDRESS OR TELEPHONE (#1079).
//
// The event documents — tournaments, the series, their setups and drafts — are
// whole JSON blobs in `padel24_storage`, written through `/kv`. The engine
// builds their participants by copying the club's player object
// (`{ ...p, startElo: p.elo }`, `groups[].players.push(p)`,
// `{ ...replacementPlayer }`), and the club's player object carries `email` and
// `phone`. So every tournament, and every archived tournament for as long as the
// archive is kept, held another copy of each player's contact details — which
// then travelled to every client that loaded it, and which erasure (#1062)
// cannot reach column by column.
//
// ── WHY THIS REMOVES TWO FIELDS RATHER THAN KEEPING A LIST ───────
//
// A participant whitelist (id, name, elo, gender, …) was the first design and is
// the wrong one here, because the engine reads more of the copied player than a
// list written today would name: the newbie and inactivity boosts read
// `matchesPlayed` and `lastMatchAt`, the mix formats `gender`, the formats add
// `startElo`, `isPlaceholder`, `injured` and more, and the player object has an
// open `extra` tail of legacy fields nobody has catalogued. Dropping any of
// those would move a rating, which is the side effect CLAUDE.md forbids.
//
// The two contact fields are the opposite case, and it is MEASURED, not hoped:
//
//   • no engine module reads `email` or `phone` off a participant (the only
//     reads in packages/engine are the demo player bank, which generates them,
//     and a booking's `bookedBy`, which is not in these documents);
//   • tests/event-document-contact.test.ts builds every format through the real
//     engine from players carrying both, runs rounds, results and a
//     replacement, and finds them ONLY at `players[]`, `groups[].players[]` —
//     plus `standbyReserves[]` (the browser's activity → tournament step) and a
//     setup's custom players — and asserts that removing them leaves every other
//     byte of the document identical.
//
// So removing exactly these two is provably engine-neutral, and a whitelist is
// not.
//
// ── WHAT IS DELIBERATELY LEFT ALONE ──────────────────────────────
//
// `club` — the organization's own profile, which the Padel24 activity path
// copies whole into the tournament. Its telephone is the club's, shown to the
// public in the member app; it is not a participant, and narrowing it is not
// this change's decision. Nothing in these documents is a booking's `bookedBy`
// (a series match keeps only date, time and court in `bookingInfo`).
//
// ── WHERE IT RUNS ────────────────────────────────────────────────
//
// At the two ends of the one door these documents go through: the browser's
// `Storage.set` (every writer — the persist effects, the archive, the pad
// actions, the setups — passes it), and the API's `PUT /kv/:key`, so an older
// bundle cannot put the fields back. `GET /kv/:key` applies it to the response,
// so a document stored before this stops travelling with them; the row itself
// is scrubbed by packages/db/scripts/scrub-event-document-contact.ts, which is
// separate, dry-run by default, and never runs on its own.
//
// PURE: no Storage, no clock — the browser and the API run the same function.
// ─────────────────────────────────────────────────────────────────────

/** The fields removed from every participant in an event document. */
export const PARTICIPANT_CONTACT_FIELDS = ['email', 'phone'] as const;

const CONTACT = new Set<string>(PARTICIPANT_CONTACT_FIELDS);

/** The organization's own profile, copied into a Padel24 tournament. Not a participant. */
const ORGANIZATION_PROFILE_KEY = 'club';

/** Event documents by exact key: the live and parked tournaments and series. */
const EVENT_DOCUMENT_KEYS = new Set([
  'active_tournament_v1',
  'paused_tournaments_v1',
  'active_series_v1',
  'paused_series_v1',
]);

/** Archived tournaments: one `tournament:<id>` row each. */
const ARCHIVED_TOURNAMENT_PREFIX = 'tournament:';

/**
 * Whether `key` holds an event document — a tournament, the series, an archived
 * tournament, or a setup or draft that will become one.
 *
 * The LOGICAL key, as the browser names it and as `/kv` stores it.
 */
export function isEventDocumentKey(key: string): boolean {
  if (EVENT_DOCUMENT_KEYS.has(key)) return true;
  if (key.startsWith(ARCHIVED_TOURNAMENT_PREFIX)) return true;
  // `padel24_setups_v1`, `km_setups_v1`, `klubbmesterskap_setups_v1`, …
  if (/^[a-z0-9_]+_setups_v1$/.test(key)) return true;
  // `draft_padel24_v1`, `draft_americano_v1`, …
  return /^draft_[a-z0-9_]+_v1$/.test(key);
}

/**
 * `doc` with `email` and `phone` removed from every object in it, except the
 * organization's own profile under `club`.
 *
 * STRUCTURALLY SHARED: what did not change is returned as the same reference,
 * and a document with nothing to remove comes back as `doc` itself — so a
 * caller can tell «nothing to do» by identity, and key order and every other
 * value are untouched.
 */
export function withoutParticipantContact<T>(doc: T): T {
  return strip(doc) as T;
}

function strip(value: unknown): unknown {
  if (Array.isArray(value)) {
    let out: unknown[] | null = null;
    for (let i = 0; i < value.length; i++) {
      const next = strip(value[i]);
      if (next !== value[i]) {
        out ??= value.slice();
        out[i] = next;
      }
    }
    return out ?? value;
  }
  if (value === null || typeof value !== 'object') return value;

  const entries = Object.entries(value as Record<string, unknown>);
  let changed = false;
  const kept: [string, unknown][] = [];
  for (const [key, child] of entries) {
    if (CONTACT.has(key)) {
      changed = true;
      continue;
    }
    if (key === ORGANIZATION_PROFILE_KEY) {
      kept.push([key, child]);
      continue;
    }
    const next = strip(child);
    if (next !== child) changed = true;
    kept.push([key, next]);
  }
  // `fromEntries`, not assignment: a parsed document may carry an own
  // `__proto__` key, which assignment would turn into a prototype.
  return changed ? Object.fromEntries(kept) : value;
}

/**
 * How many contact fields `doc` holds outside `club` — what the strip would
 * remove. For reports; never the values.
 */
export function participantContactFieldCount(doc: unknown): number {
  if (Array.isArray(doc))
    return doc.reduce<number>((n, x) => n + participantContactFieldCount(x), 0);
  if (doc === null || typeof doc !== 'object') return 0;
  let n = 0;
  for (const [key, child] of Object.entries(doc as Record<string, unknown>)) {
    if (CONTACT.has(key)) n++;
    else if (key !== ORGANIZATION_PROFILE_KEY) n += participantContactFieldCount(child);
  }
  return n;
}

/**
 * The serialized document for `key`, without participant contact.
 *
 * Returns `value` UNCHANGED — the same string, byte for byte — for a key that is
 * not an event document, for a value that is not JSON (the empty string the
 * browser writes for «no active tournament» is one), and for a document with
 * nothing to remove. Only a document that actually carried contact is
 * re-serialized. A malformed blob therefore stays malformed: a reader that
 * refuses an unreadable document must keep seeing one.
 */
export function withoutParticipantContactValue(key: string, value: string): string {
  if (!isEventDocumentKey(key)) return value;
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return value;
  }
  const stripped = strip(parsed);
  return stripped === parsed ? value : JSON.stringify(stripped);
}
